/**
 * NotesEditor (NOTES-EDITOR, Seth 2026-06-25) — the shared notes field for all
 * of Pegasus (dispatch notes, internal notes, job description, and the mobile
 * tech app). Three things on top of a plain textarea:
 *   1. Native browser spell-check (free, and the phone keyboard adds its own).
 *   2. AI assist — Clean up / Summarize / Fix spelling via the `polish-note`
 *      edge function (Gemini). PROPOSAL-style: the suggestion shows for
 *      Accept/Discard, never a silent overwrite ([[derive_dont_flag]] spirit).
 *   3. Voice-to-text mic (Web Speech API) — the big convenience win for techs
 *      in the field: talk, then tap "Clean up" to professionalize it.
 *
 * Reuse everywhere instead of a bare <textarea>. Mobile-friendly (wrapping,
 * tappable controls). Degrades gracefully: no mic button where speech isn't
 * supported; AI buttons surface a quiet error if the helper is unreachable.
 */
import { useEffect, useRef, useState } from "react";
import { Wand2, Sparkles, SpellCheck, Mic, Check, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";

type Action = "cleanup" | "summarize" | "spelling";

const ACTION_LABEL: Record<Action, string> = {
  cleanup: "Clean up",
  summarize: "Summarize",
  spelling: "Fix spelling",
};

/**
 * Light, DETERMINISTIC tidy that runs automatically on blur (Seth 2026-06-26:
 * "auto-fix capitalization/punctuation/grammar without hitting buttons — those
 * get forgotten"). Mechanical only — capitalizes sentence starts + standalone
 * "i", collapses runs of spaces, trims trailing space. It NEVER re-words, so it
 * can't mangle a tech's readings (model numbers, "R-410A", "5.99 amps"). Deeper
 * grammar/spelling stays the AI "Clean up" tap, which proposes (never silently
 * overwrites) so it can't change a measurement behind your back.
 */
export function tidyText(s: string): string {
  if (!s) return s;
  let t = s.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\s+$/g, "");
  // Capitalize the first letter of the text + after . ! ? + after a newline.
  t = t.replace(/(^\s*|[.!?]\s+|\n\s*)([a-z])/g, (_m, pre, ch) => pre + ch.toUpperCase());
  // Standalone "i" → "I" (won't touch i inside words like "API" or "wi-fi").
  t = t.replace(/\bi\b/g, "I");
  return t;
}

// Web Speech API isn't in lib.dom's types — keep a tiny local shape.
type SpeechRec = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function getSpeechCtor(): (new () => SpeechRec) | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => SpeechRec) | null;
}

export function NotesEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  className,
  disabled = false,
  autoFocus = false,
  autoTidy = true,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Light mechanical tidy (caps/spacing) on blur — on by default everywhere. */
  autoTidy?: boolean;
}) {
  const [busy, setBusy] = useState<Action | null>(null);
  const [proposal, setProposal] = useState<{ action: Action; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRec | null>(null);
  const speechSupported = typeof window !== "undefined" && !!getSpeechCtor();

  // Stop any live dictation if the editor unmounts.
  useEffect(() => () => recRef.current?.stop(), []);

  const polish = async (action: Action) => {
    const text = value.trim();
    if (!text || busy) return;
    setBusy(action);
    setError(null);
    setProposal(null);
    try {
      const invokeOnce = () =>
        supabase.functions.invoke("polish-note", { body: { text, action } });
      let { data, error: err } = await invokeOnce();
      if (err) {
        // The edge function cold-starts on the first tap of a session and can
        // miss — retry once so the tech just waits a beat instead of erroring.
        await new Promise((r) => setTimeout(r, 800));
        ({ data, error: err } = await invokeOnce());
      }
      if (err) throw err;
      const result = String((data as { result?: string })?.result ?? "").trim();
      if (!result) setError("No suggestion came back — try again.");
      else if (result === text) setError("Looks good already — no changes suggested.");
      else setProposal({ action, text: result });
    } catch {
      setError("Couldn't reach the AI helper. Your note is unchanged.");
    } finally {
      setBusy(null);
    }
  };

  const acceptProposal = () => {
    if (proposal) onChange(proposal.text);
    setProposal(null);
  };

  const toggleMic = () => {
    if (listening) {
      recRef.current?.stop();
      setListening(false);
      return;
    }
    const Ctor = getSpeechCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    // Anchor on whatever is already typed; append finals, show interims live.
    let base = value;
    rec.onresult = (e) => {
      let finalT = "";
      let interimT = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        const t = r[0]?.transcript ?? "";
        if (r.isFinal) finalT += t;
        else interimT += t;
      }
      if (finalT) base = (base ? `${base} ` : "") + finalT.trim();
      onChange((base + (interimT ? ` ${interimT}` : "")).trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setError(null);
    rec.start();
    setListening(true);
  };

  const ToolBtn = ({
    action,
    icon: Icon,
  }: {
    action: Action;
    icon: typeof Wand2;
  }) => (
    <button
      type="button"
      onClick={() => polish(action)}
      disabled={disabled || !value.trim() || busy !== null || listening}
      title={
        action === "cleanup"
          ? "Fix spelling + grammar and make it read professionally"
          : action === "summarize"
            ? "Condense to a short summary"
            : "Correct only spelling/typos"
      }
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-white hover:text-brand-700 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:shadow-none"
    >
      {busy === action ? <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-600" /> : <Icon className="h-3.5 w-3.5 text-slate-400" />}
      {ACTION_LABEL[action]}
    </button>
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* One unified control: the textarea AND its action bar live inside the
          same bordered box, so the AI/voice tools clearly belong to the note
          (Seth: the buttons didn't read as part of the notes field). */}
      <div
        className={cn(
          "overflow-hidden rounded-lg border bg-white shadow-sm transition-colors",
          "focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-100",
          listening ? "border-rose-300 ring-2 ring-rose-200" : "border-hairline",
          disabled && "opacity-60",
        )}
      >
        <div className="relative">
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => {
              if (!autoTidy || listening) return;
              const t = tidyText(value);
              if (t !== value) onChange(t);
            }}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            autoFocus={autoFocus}
            spellCheck
            className="block w-full resize-y border-0 bg-transparent px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
          />
          {listening && (
            <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Listening
            </span>
          )}
        </div>

        {/* Action bar — sits inside the box, on a tinted strip under a divider. */}
        <div className="flex flex-wrap items-center gap-0.5 border-t border-hairline bg-slate-50/80 px-1.5 py-1">
          {speechSupported && (
            <>
              <button
                type="button"
                onClick={toggleMic}
                disabled={disabled || busy !== null}
                title={listening ? "Stop dictation" : "Dictate with your voice"}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                  listening
                    ? "bg-rose-100 text-rose-700 hover:bg-rose-200"
                    : "text-slate-600 hover:bg-white hover:text-brand-700 hover:shadow-sm",
                )}
              >
                <Mic className={cn("h-3.5 w-3.5", !listening && "text-slate-400")} />
                {listening ? "Stop" : "Dictate"}
              </button>
              <span aria-hidden className="mx-0.5 h-4 w-px bg-slate-200" />
            </>
          )}
          <ToolBtn action="cleanup" icon={Wand2} />
          <ToolBtn action="summarize" icon={Sparkles} />
          <ToolBtn action="spelling" icon={SpellCheck} />
          <span className="ml-auto hidden select-none items-center gap-1 pr-1 text-[10px] font-medium uppercase tracking-wider text-slate-400 sm:inline-flex">
            <Sparkles className="h-3 w-3" /> AI assist
          </span>
        </div>
      </div>

      {error && <p className="text-xs text-rose-600">{error}</p>}

      {/* AI proposal — review before it replaces the note (never silent). */}
      {proposal && (
        <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-brand-800">
            <Sparkles className="h-3.5 w-3.5" /> Suggested ({ACTION_LABEL[proposal.action]})
          </div>
          <p className="whitespace-pre-wrap rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-800">
            {proposal.text}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={acceptProposal}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-700"
            >
              <Check className="h-3.5 w-3.5" /> Use this
            </button>
            <button
              type="button"
              onClick={() => setProposal(null)}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              <X className="h-3.5 w-3.5" /> Keep mine
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
